import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import Header from './components/Header.jsx';
import Portfolio from './components/Portfolio.jsx';
import ResearchHub from './components/ResearchHub.jsx';
import Screener from './components/Screener.jsx';
import Sidebar from './components/Sidebar.jsx';
import TrustCompliance from './components/TrustCompliance.jsx';
import { AlertsPage, LearnPage } from './components/UtilityPages.jsx';
import WatchlistWorkspace from './components/WatchlistWorkspace.jsx';
import { cloneDemoMarket, indexSnapshot } from './data/marketData.js';
import { safeLocalStorage } from './utils/market.js';
import { applySeoForView, getRoutePath, getViewForPath } from './seo.js';

const Dashboard = lazy(() => import('./components/Dashboard.jsx'));
const NewsPage = lazy(() => import('./components/NewsPage.jsx'));
const SupportPage = lazy(() => import('./components/SupportPage.jsx'));
const StockDrawer = lazy(() => import('./components/StockDrawer.jsx'));

const initialPortfolio = {
  cash: 794205,
  holdings: {
    HDFCBANK: { quantity: 45, averagePrice: 1842.5 },
    TCS: { quantity: 28, averagePrice: 3198.1 },
    RELIANCE: { quantity: 20, averagePrice: 1411.2 },
  },
};

const STORAGE_KEYS = {
  watchlist: 'vizmarkets-watchlist-v1',
  alerts: 'vizmarkets-alerts-v1',
  portfolio: 'vizmarkets-paper-v1',
};

// Retain these keys only for one-way migration of data saved before the VizMarkets rebrand.
const LEGACY_STORAGE_KEYS = {
  watchlist: ['vizmex-watchlist-v1'],
  alerts: ['vizmex-alerts-v1'],
  portfolio: ['vizmex-paper-v1'],
};

function getIndiaMarketStatus() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  const weekday = get('weekday');
  const minutes = Number(get('hour')) * 60 + Number(get('minute'));
  if (weekday === 'Sat' || weekday === 'Sun') return { label: 'Market closed', tone: 'closed' };
  if (minutes >= 555 && minutes < 930) return { label: 'Market open', tone: 'open' };
  if (minutes >= 540 && minutes < 555) return { label: 'Pre-open session', tone: 'preopen' };
  return { label: 'Market closed', tone: 'closed' };
}

export default function App() {
  const [activeView, setActiveView] = useState(() => getViewForPath(window.location.pathname) || 'overview');
  const [period, setPeriod] = useState('1D');
  const [stocks, setStocks] = useState(cloneDemoMarket);
  const [indices, setIndices] = useState(indexSnapshot);
  const [dataMeta, setDataMeta] = useState({ status: 'demo', provider: 'Demo snapshot', delayed: true, asOf: new Date().toISOString() });
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [watchlist, setWatchlist] = useState(() => safeLocalStorage.readMigrated(STORAGE_KEYS.watchlist, LEGACY_STORAGE_KEYS.watchlist, ['HDFCBANK', 'TCS', 'RELIANCE', 'M&M']));
  const [alerts, setAlerts] = useState(() => safeLocalStorage.readMigrated(STORAGE_KEYS.alerts, LEGACY_STORAGE_KEYS.alerts, []));
  const [portfolio, setPortfolio] = useState(() => safeLocalStorage.readMigrated(STORAGE_KEYS.portfolio, LEGACY_STORAGE_KEYS.portfolio, initialPortfolio));

  const navigateTo = useCallback((view, options = {}) => {
    const nextPath = getRoutePath(view);
    if (window.location.pathname !== nextPath) {
      window.history[options.replace ? 'replaceState' : 'pushState']({ view }, '', nextPath);
    }
    setSelectedSymbol(null);
    setActiveView(view);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const marketStatus = getIndiaMarketStatus();
  const selectedStock = stocks.find((stock) => stock.symbol === selectedSymbol) || null;

  const fetchMarket = useCallback(async (force = false) => {
    setRefreshing(true);
    try {
      const response = await fetch(`/api/market${force ? '?refresh=1' : ''}`);
      if (!response.ok) throw new Error('Provider unavailable');
      const payload = await response.json();
      setStocks(payload.stocks);
      setIndices(payload.indices);
      setDataMeta({ status: payload.status, provider: payload.provider, delayed: payload.delayed, asOf: payload.asOf });
      if (force) setNotice('Market snapshot refreshed. Provider data may be delayed.');
    } catch {
      setDataMeta((current) => ({ ...current, status: 'demo', provider: 'Demo snapshot' }));
      if (force) setNotice('Provider unavailable — showing a clearly labeled demo snapshot.');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchMarket(false); }, [fetchMarket]);
  useEffect(() => {
    const syncViewFromHistory = () => {
      const nextView = getViewForPath(window.location.pathname);
      if (nextView) {
        setSelectedSymbol(null);
        setActiveView(nextView);
      } else {
        window.history.replaceState({ view: 'overview' }, '', getRoutePath('overview'));
        setActiveView('overview');
      }
    };
    if (!getViewForPath(window.location.pathname)) {
      window.history.replaceState({ view: 'overview' }, '', getRoutePath('overview'));
    }
    window.addEventListener('popstate', syncViewFromHistory);
    return () => window.removeEventListener('popstate', syncViewFromHistory);
  }, []);
  useEffect(() => { applySeoForView(activeView); }, [activeView]);
  useEffect(() => { safeLocalStorage.write(STORAGE_KEYS.watchlist, watchlist); }, [watchlist]);
  useEffect(() => { safeLocalStorage.write(STORAGE_KEYS.alerts, alerts); }, [alerts]);
  useEffect(() => { safeLocalStorage.write(STORAGE_KEYS.portfolio, portfolio); }, [portfolio]);
  useEffect(() => {
    if (!notice) return undefined;
    const id = window.setTimeout(() => setNotice(''), 4200);
    return () => window.clearTimeout(id);
  }, [notice]);

  const toggleWatchlist = (symbol) => setWatchlist((current) => current.includes(symbol) ? current.filter((item) => item !== symbol) : [...current, symbol]);
  const addAlert = (symbol, price) => {
    if (!Number.isFinite(price) || price <= 0) return;
    setAlerts((current) => [...current, { id: `${symbol}-${Date.now()}`, symbol, price }]);
    setNotice(`Alert saved for ${symbol} above ₹${price.toLocaleString('en-IN')}.`);
  };
  const paperTrade = (stock, quantity, type) => {
    const amount = stock.price * quantity;
    setPortfolio((current) => {
      const holdings = { ...current.holdings };
      const existing = holdings[stock.symbol] || { quantity: 0, averagePrice: stock.price };
      if (type === 'BUY') {
        if (amount > current.cash) { setNotice('Not enough virtual cash for this simulation.'); return current; }
        const nextQuantity = existing.quantity + quantity;
        holdings[stock.symbol] = { quantity: nextQuantity, averagePrice: ((existing.quantity * existing.averagePrice) + amount) / nextQuantity };
        setNotice(`Simulated purchase of ${quantity} ${stock.symbol}.`);
        return { cash: current.cash - amount, holdings };
      }
      if (existing.quantity < quantity) { setNotice('Not enough virtual quantity to simulate this sale.'); return current; }
      const nextQuantity = existing.quantity - quantity;
      if (nextQuantity === 0) delete holdings[stock.symbol]; else holdings[stock.symbol] = { ...existing, quantity: nextQuantity };
      setNotice(`Simulated sale of ${quantity} ${stock.symbol}.`);
      return { cash: current.cash + amount, holdings };
    });
  };

  const watchlistStocks = useMemo(() => stocks.filter((stock) => watchlist.includes(stock.symbol)), [stocks, watchlist]);

  let content;
  if (activeView === 'overview') content = <Dashboard stocks={stocks} period={period} onPeriodChange={setPeriod} onSelectStock={setSelectedSymbol} onNavigate={navigateTo} dataMeta={dataMeta} />;
  else if (activeView === 'map') content = <Dashboard mapOnly stocks={stocks} period={period} onPeriodChange={setPeriod} onSelectStock={setSelectedSymbol} onNavigate={navigateTo} dataMeta={dataMeta} />;
  else if (activeView === 'screener') content = <Screener stocks={stocks} onSelectStock={setSelectedSymbol} watchlist={watchlist} onToggleWatchlist={toggleWatchlist} />;
  else if (activeView === 'news') content = <NewsPage stocks={stocks} onSelectStock={setSelectedSymbol} />;
  else if (activeView === 'watchlist') content = <WatchlistWorkspace stocks={watchlistStocks} alerts={alerts} onSelectStock={setSelectedSymbol} onToggleWatchlist={toggleWatchlist} onRemoveAlert={(id) => setAlerts((current) => current.filter((item) => item.id !== id))} onNavigate={navigateTo} />;
  else if (activeView === 'research') content = <ResearchHub />;
  else if (activeView === 'portfolio') content = <Portfolio stocks={stocks} portfolio={portfolio} onSelectStock={setSelectedSymbol} onReset={() => setPortfolio(initialPortfolio)} />;
  else if (activeView === 'alerts') content = <AlertsPage alerts={alerts} stocks={stocks} onRemove={(id) => setAlerts((current) => current.filter((item) => item.id !== id))} />;
  else if (activeView === 'learn') content = <LearnPage />;
  else if (activeView === 'support') content = <SupportPage />;
  else content = <TrustCompliance />;

  return (
    <div className="app-shell">
      <Header indices={indices} dataMeta={dataMeta} marketStatus={marketStatus} onRefresh={() => fetchMarket(true)} refreshing={refreshing} stocks={stocks} onSelectStock={setSelectedSymbol} onToggleMenu={() => setSidebarOpen(true)} onNavigate={navigateTo} />
      <Sidebar activeView={activeView} onNavigate={navigateTo} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="app-main"><Suspense fallback={<div className="page-loading">Preparing your market workspace…</div>}>{content}</Suspense></main>
      <footer className="site-footer" aria-label="Site information">
        <p>© {new Date().getFullYear()} <a href="https://vizmarkets.com/">VizMarkets.com</a> · All rights reserved.</p>
        <nav className="footer-community-links" aria-label="Support and social">
          <a href={getRoutePath('support')} aria-current={activeView === 'support' ? 'page' : undefined}>Support VizMarkets</a>
          <a href="https://x.com/vizmarkets" target="_blank" rel="noopener noreferrer"><svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.64 7.584H.47l8.6-9.835L0 1.154h7.594l5.243 6.932 6.064-6.933ZM17.61 20.644h2.039L6.486 3.24H4.298L17.61 20.644Z" /></svg>Follow us on X<span className="support-sr-only"> (opens in a new tab)</span></a>
        </nav>
        <p>A product of <a href="https://techsivam.in/" target="_blank" rel="noreferrer">Techsivam Labs</a></p>
      </footer>
      <footer className="global-disclaimer"><ShieldIcon /> <span><strong>Educational market analytics.</strong> Not investment advice. Prices may be delayed. “Research” and personalised advice remain gated until the applicable SEBI registration and operating controls are in place.</span></footer>
      {selectedStock && <Suspense fallback={null}><StockDrawer stock={selectedStock} onClose={() => setSelectedSymbol(null)} saved={watchlist.includes(selectedStock.symbol)} onToggleWatchlist={toggleWatchlist} onPaperTrade={paperTrade} onAddAlert={addAlert} dataMeta={dataMeta} /></Suspense>}
      {notice && <div className="toast" role="status">{notice}</div>}
    </div>
  );
}

function ShieldIcon() {
  return <span className="footer-shield" aria-hidden="true">✓</span>;
}
